import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';

interface ModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Called when modal should close */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Modal body */
  children: ReactNode;
  /** Footer actions */
  footer?: ReactNode;
  /** Max width class */
  maxWidth?: string;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = 'max-w-lg',
}: ModalProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className={cn(
        'w-full rounded-xl border border-border bg-surface p-0 shadow-xl backdrop:bg-black/40',
        maxWidth,
      )}
      onClose={onClose}
    >
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1 text-text-tertiary transition-colors hover:bg-surface-tertiary hover:text-text-primary"
          aria-label={t('common.close')}
        >
          ✕
        </button>
      </div>
      <div className="px-6 py-4">{children}</div>
      {footer && (
        <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
          {footer}
        </div>
      )}
    </dialog>
  );
}
